import { createSelector } from 'reselect';

import { selectFlowsOnSiteEnabled } from '../../flow/selectors/FlowSelectors';
import { FeatureFlags } from '../../ts/FeatureFlags';
import {
  BackgroundJobDefinition,
  DataRoleDefinition,
  DatasourceDefinition,
  FlowDefinition,
  MetricDefinition,
  ProjectDefinition,
  ViewDefinition,
  WorkbookDefinition,
} from '../../ts/models/ContentDefinitions';
import { ContentFilterKeyName } from '../../ts/models/ContentFilters';
import { ExplorableItemType } from '../../ts/models/ContentType';
import { ExploreContentApi_ContentType } from '../../ts/ServerApi/types/IContent';
import { IFilterClause, IStringValuedFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { AppState } from '../state/AppState';
import { FilterMap } from '../state/ContentExplorerState';
import { convertSiteContentTypeToContentType, SiteSpaceContentType } from '../state/SiteSpaceContentTypeState';
import {
  isBackgroundJobsSpace,
  isFavoritesSpace,
  isMetricSpace,
  isRecentsSpace,
  isRecommendationsSpace,
  isSearchSpace,
  isSiteDataRoleSubspace,
  isSiteDatasourceSubspace,
  isSiteSpace,
  IsWorkbookDatasourceSubspaceTab,
} from '../state/SpaceInfo';
import { selectSpaceInfo } from './ContentSelectors';

// ---------- for project space
// Remove with removal of FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled
export const defaultContentTypeFilterValuesWithoutDataRoles: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DatasourceDefinition.contentType,
];

// Remove with removal of FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled
export const defaultContentTypeFilterValuesWithFlowsButWithoutDataRoles: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DatasourceDefinition.contentType,
  FlowDefinition.contentType,
];

export const defaultContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DataRoleDefinition.contentType,
  DatasourceDefinition.contentType,
];

export const defaultContentTypeFilterValuesWithFlows: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DatasourceDefinition.contentType,
  DataRoleDefinition.contentType,
  FlowDefinition.contentType,
];

// ----- for favorites space (includes Views, which project space does not)

// Remove with removal of FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled
export const defaultContentTypeFilterValuesForFavoritesSpaceWithoutDataRolesOrFlows: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DatasourceDefinition.contentType,
  ViewDefinition.contentType
];

// Remove with removal of FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled
export const defaultContentTypeFilterValuesForFavoritesSpaceWithoutDataRoles: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DatasourceDefinition.contentType,
  FlowDefinition.contentType,
  ViewDefinition.contentType
];

export const defaultContentTypeFilterValuesForFavoritesSpaceWithoutFlows: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DataRoleDefinition.contentType,
  DatasourceDefinition.contentType,
  ViewDefinition.contentType
];

export const defaultContentTypeFilterValuesForFavoritesSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
  WorkbookDefinition.contentType,
  DatasourceDefinition.contentType,
  DataRoleDefinition.contentType,
  FlowDefinition.contentType,
  ViewDefinition.contentType
];

export const defaultContentTypeFilterValuesForDatasourcesSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  DatasourceDefinition.contentType
];

export const defaultContentTypeFilterValuesForDataRolesSubspace: ReadonlyArray<ExploreContentApi_ContentType> = [
  ProjectDefinition.contentType,
];

export const defaultContentTypeFilterValuesForRecentsSpace: ReadonlyArray<ExploreContentApi_ContentType> = [
  ViewDefinition.contentType
];

// Content type filter is unused for the search or recommendation space
export const defaultEmptyContentTypeFilterValues: ReadonlyArray<ExploreContentApi_ContentType> = [];

export const defaultContentTypeFilterValuesForJobsSpace: ReadonlyArray<ExplorableItemType> = [
  BackgroundJobDefinition.itemType
];

export const defaultContentTypeFilterValuesForMetricsSpace: ReadonlyArray<ExplorableItemType> = [
  MetricDefinition.itemType
];

export function selectFavoriteContentTypes(state: AppState): ReadonlyArray<ExploreContentApi_ContentType> {
  if (selectFlowsOnSiteEnabled(state)) {
    return FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? defaultContentTypeFilterValuesForFavoritesSpace : defaultContentTypeFilterValuesForFavoritesSpaceWithoutDataRoles;
  } else {
    return FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? defaultContentTypeFilterValuesForFavoritesSpaceWithoutFlows : defaultContentTypeFilterValuesForFavoritesSpaceWithoutDataRolesOrFlows;
  }

}

export function selectDefaultContentTypeFilterValues(state: AppState) {

  const spaceInfo = selectSpaceInfo(state);
  if (isSiteDataRoleSubspace(spaceInfo)) {
    return defaultContentTypeFilterValuesForDataRolesSubspace;
  }

  if (IsWorkbookDatasourceSubspaceTab(spaceInfo) || isSiteDatasourceSubspace(spaceInfo)) {
    return defaultContentTypeFilterValuesForDatasourcesSubspace;
  }

  if (isSiteDatasourceSubspace(spaceInfo)) {
    return defaultContentTypeFilterValuesForDatasourcesSubspace;
  }

  if (isFavoritesSpace(spaceInfo)) {
    return selectFavoriteContentTypes(state);
  }

  if (isRecentsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForRecentsSpace;
  }

  if (isSearchSpace(spaceInfo) || isRecommendationsSpace(spaceInfo)) {
    return defaultEmptyContentTypeFilterValues;
  }

  if (isBackgroundJobsSpace(spaceInfo)) {
    return defaultContentTypeFilterValuesForJobsSpace;
  }

  if(isMetricSpace(spaceInfo)){
    return defaultContentTypeFilterValuesForMetricsSpace;
  }

  if (selectFlowsOnSiteEnabled(state)) {
    return FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? defaultContentTypeFilterValuesWithFlows : defaultContentTypeFilterValuesWithFlowsButWithoutDataRoles;
  } else {
    return FeatureFlags.nlsemanticsServiceCustomDataRoleEnabled ? defaultContentTypeFilterValues : defaultContentTypeFilterValuesWithoutDataRoles;
  }
}

export function selectProjectSpaceContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  return state.contentExplorer.contentTypeFilterValues;
}

export function selectEffectiveProjectSpaceContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  return state.contentExplorer.contentTypeFilterValues || selectDefaultContentTypeFilterValues(state);
}

export function selectMixedContentFilterClauses(state: AppState): FilterMap {
  return state.contentExplorer.mixedContentFilterClauses;
}

export function selectSearchText(state: AppState): string {
  return (state.contentExplorer.mixedContentFilterClauses && state.contentExplorer.mixedContentFilterClauses.search &&
    (state.contentExplorer.mixedContentFilterClauses.search as IStringValuedFilterClause).value)
    || '';
}

export function selectSpecificMixedContentFilterClause(state: AppState, filterType: ContentFilterKeyName): IFilterClause {
  return state.contentExplorer.mixedContentFilterClauses[filterType];
}

export function selectSiteSpaceContentType(state: AppState): SiteSpaceContentType {
  return state.spaceInfo.subspaceName as SiteSpaceContentType;
}

export function selectMixedContentFilters(state: AppState): FilterMap {
  return state.contentExplorer.mixedContentFilterClauses;
}

// This memoizes the array, ensuring we don't create new references unnecessarily.
const selectSiteSpaceFilterValues = createSelector(
  selectSiteSpaceContentType,
  (contentType) => [convertSiteContentTypeToContentType(contentType)]
);

export function selectCurrentlyApplicableContentTypes(state: AppState): ReadonlyArray<ExplorableItemType> {
  // This could be cleaned up by adding the current space (site or project) into the spaceInfo
  const spaceInfo = selectSpaceInfo(state);
  return isSiteSpace(spaceInfo)
    ? selectSiteSpaceFilterValues(state)
    : selectEffectiveProjectSpaceContentTypes  (state);
}
